import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import OrderCard from './OrderCard';
import { InitialUrlSearchParams } from '../../../../utils/environment';
import { IOrder, OrderHistoryResponse, IPagination, TokenMap, EmptyStateProps, SalesChannel } from './types';
import { LoadingMoreSpinner } from '../../../product-listing-page/components/SmallUIComponents';
import TablePagination from '@material-ui/core/TablePagination';
import ErrorUI from './ErrorUI';
import { aloApiAuthFetch } from '../../../../utils/alo-api-fetch';
import OrderHistoryEmptyState from './OrderHistoryEmptyState';
import OrderHistoryFiltersAndSearch from './OrderHistoryFiltersAndSearch';
import NoResultsMessage from './NoResultsMessage';
import { updateBrowserHistoryState } from './helpers';
import { generateToken } from '../../../../utils/token-generator';

const urlParams = InitialUrlSearchParams;
const getValidSalesChannel = (): SalesChannel => {
  const value = urlParams.get('channel');
  return value === 'online' || value === 'in_store' ? value : 'all';
};
const DEFAULT_ORDER_COUNT = 5;
const LIMIT_ORDER_COUNT_PER_PAGE = 15;

const orderHistoryEmptyStateConfig: Record<SalesChannel, EmptyStateProps> = {
  all: {
    heading: 'No orders yet',
    message: "You haven't placed any orders yet.\nWhen you do, they'll appear here.",
    link: '/collections/new-arrivals',
    cta: 'Start Shopping',
  },
  online: {
    message: "You have not placed any online orders yet.\nWhen you do, they'll appear here.",
    link: '/collections/new-arrivals',
    cta: 'Shop New Arrivals',
  },
  in_store: {
    message: 'You have not shopped in store yet.',
    link: '/pages/stores',
    cta: 'Find a Store',
  },
};

const OrderHistory = () => {
  const [orders, setOrders] = useState<IOrder[] | null>(null);
  const [isError, setIsError] = useState(false);
  const [paginationData, setPaginationData] = useState<IPagination | null>(null);
  const [loading, setLoading] = useState(false);
  const [tokens, setTokens] = useState<TokenMap>(new Map());

  const [currentPage, setPage] = useState(Number(urlParams.get('page')) || 1);
  const [salesChannel, setSalesChannel] = useState<SalesChannel>(getValidSalesChannel());
  const [searchQuery, setSearchQuery] = useState(urlParams.get('query') || '');
  const orderCountFromURLparams = Number(urlParams.get('count'));
  const adjustedOrderCount =
    orderCountFromURLparams < LIMIT_ORDER_COUNT_PER_PAGE ? orderCountFromURLparams : LIMIT_ORDER_COUNT_PER_PAGE; // Limit possible user input to 15 to avoid big requests
  const [orderCount, setOrderCount] = useState(adjustedOrderCount || DEFAULT_ORDER_COUNT);
  const paginationRef = useRef({ startCursor: '', endCursor: '', direction: '' });

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      const currentPaginationData = paginationRef.current;
      const cursor =
        currentPaginationData.direction && currentPaginationData.direction === 'next'
          ? `&end_cursor=${currentPaginationData.endCursor}`
          : currentPaginationData.direction
          ? `&start_cursor=${currentPaginationData.startCursor}`
          : '';

      const queryParam = searchQuery ? `&query=${encodeURIComponent(searchQuery)}` : '';
      aloApiAuthFetch(
        `/v1/orders?page=${currentPage}&page_size=${orderCount}&channel=${salesChannel}${cursor}${queryParam}`
      )
        .then((res) => res.json())
        .then((response: OrderHistoryResponse) => {
          const error = response.detail;
          if (error) {
            setIsError(true);
            console.log('Alo API error: ', error);
          } else {
            if (response.pagination?.start_cursor || response.pagination?.end_cursor)
              paginationRef.current = {
                ...paginationRef.current,
                startCursor: response.pagination.start_cursor || '',
                endCursor: response.pagination.end_cursor || '',
              };
            response.orders ? setOrders(response.orders) : setIsError(true);
            if (response.pagination) {
              setPaginationData(response.pagination);
            }
          }
          setLoading(false);
        })
        .catch((err) => {
          setIsError(true);
          console.log('Fetch error: ', err);
          setLoading(false);
        });
    };
    fetchOrders();
  }, [orderCount, currentPage, salesChannel, searchQuery]);

  // Generate tokens for all orders
  useEffect(() => {
    if (!orders) return;

    const generateAllTokens = async () => {
      const tokenMap: TokenMap = new Map();
      const email = window.getCustomerInfo?.()?.email;

      if (!email) return;

      const tokenPromises = orders.map(async (order) => {
        try {
          const orderEmail = order.customer?.email || email;
          const token = await generateToken(order.order_number, orderEmail);
          return { orderNumber: order.order_number, token };
        } catch (error) {
          console.error(`Failed to generate token for order ${order.order_number}:`, error);
          return null;
        }
      });

      const results = await Promise.all(tokenPromises);
      results.forEach((result) => {
        if (result && result.token) {
          tokenMap.set(result.orderNumber, result.token);
        }
      });

      setTokens(tokenMap);
    };

    generateAllTokens();
  }, [orders]);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    paginationRef.current = { ...paginationRef.current, direction: currentPage > newPage + 1 ? 'prev' : 'next' };
    setPage(newPage + 1);
    updateBrowserHistoryState({ page: (newPage + 1).toString() });
  };

  const [rowsPerPage, setRowsPerPage] = React.useState(orderCount);

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = parseInt(event.target.value, 10);
    setRowsPerPage(newValue);
    setOrderCount(newValue);
    setPage(1);
    updateBrowserHistoryState({ page: '1', count: newValue.toString() });
  };

  const handleFiltersChange = useCallback(
    (filters: { salesChannel: SalesChannel; searchQuery: string; page: number }) => {
      setSalesChannel(filters.salesChannel);
      setSearchQuery(filters.searchQuery);
      setPage(filters.page);
      paginationRef.current = { startCursor: '', endCursor: '', direction: '' };
    },
    []
  );

  const renderContent = () => {
    if (loading || !orders) return <LoadingMoreSpinner />;
    if (orders.length === 0 && !searchQuery)
      return <OrderHistoryEmptyState {...orderHistoryEmptyStateConfig[salesChannel]} />;
    if (orders.length === 0 && searchQuery) return <NoResultsMessage searchQuery={searchQuery} />;
    return orders.map((order) => (
      <OrderCard key={order.order_id} order={order} token={tokens.get(order.order_number)} />
    ));
  };

  if (isError) return <ErrorUI />;

  const isEmptyState = orders !== null && orders.length === 0 && !searchQuery;
  const hasNoOrders = isEmptyState && salesChannel === 'all';
  const shouldShowPagination =
    !loading && !isEmptyState && paginationData?.total_pages && paginationData.total_pages > 1;

  return (
    <>
      {!hasNoOrders && (
        <OrderHistoryFiltersAndSearch onFiltersChange={handleFiltersChange} hideSearchOnMobile={isEmptyState} />
      )}
      <div className={`order-history-wrapper${isEmptyState ? ' align-items-center' : ''}`}>{renderContent()}</div>
      {shouldShowPagination ? (
        <TablePagination
          component="div"
          count={paginationData.total_items}
          page={currentPage - 1}
          onChangePage={handleChangePage}
          rowsPerPage={rowsPerPage}
          labelRowsPerPage="Orders per page:"
          onChangeRowsPerPage={handleChangeRowsPerPage}
          rowsPerPageOptions={Array.from(new Set([5, 10, 15, rowsPerPage])).sort((a, b) => a - b)}
        />
      ) : null}
    </>
  );
};

export default OrderHistory;

const renderOrderHistory = () => {
  const mountNode = document.querySelector('[react-render-target="order-history"]');
  if (mountNode) ReactDOM.render(<OrderHistory />, mountNode);
  else console.warn('missing render target: order-history');
};

export const InitOrderHistory = () => {
  renderOrderHistory();
};
